import { useEffect, useMemo, useState } from "react";
import type {
  MemberCommunicationDto,
  TeacherOccurrenceDetailDto
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../../shared/api/client";
import { fetchTeacherMemberCommunications } from "../../shared/api/memberCommunication";
import { fetchTeacherOccurrence, fetchTeacherSchedule } from "../../shared/api/teacher";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeTenantEntityType } from "../../shared/lib/uiLabels";
import {
  buildTeacherPageLinks,
  TeacherAccessState,
  TeacherScheduleItemCard
} from "./teacherUi";
import { TeacherOccurrenceCommunicationPanel } from "./TeacherOccurrenceCommunicationPanel";
import { MemberCommunicationCard } from "../../shared/ui/MemberCommunicationCard";

export function TeacherOccurrenceDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", occurrenceId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [occurrence, setOccurrence] = useState<TeacherOccurrenceDetailDto | null>(null);
  const [communications, setCommunications] = useState<{ items: MemberCommunicationDto[] } | null>(null);
  const [timeZone, setTimeZone] = useState("Asia/Tokyo");
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasTeacherRole = Boolean(session?.member?.roles.includes("teacher"));
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const occurrenceLabel = describeTenantEntityType("occurrence", tenantLabels);
  const pageLinks = buildTeacherPageLinks(hasAdminRole, tenantLabels);

  useEffect(() => {
    if (!isSessionReady || !hasTeacherRole || !occurrenceId) {
      return;
    }

    let cancelled = false;

    async function loadDetail() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const [nextOccurrence, scheduleResult, communicationsResult] = await Promise.all([
          fetchTeacherOccurrence(tenantSlug, occurrenceId),
          fetchTeacherSchedule(tenantSlug).then((value) => ({ status: "fulfilled" as const, value })).catch((reason) => ({
            status: "rejected" as const,
            reason
          })),
          fetchTeacherMemberCommunications(tenantSlug).then((value) => ({ status: "fulfilled" as const, value })).catch((reason) => ({
            status: "rejected" as const,
            reason
          }))
        ]);

        if (cancelled) {
          return;
        }

        setOccurrence(nextOccurrence);
        setTimeZone(scheduleResult.status === "fulfilled" ? scheduleResult.value.timeZone : "Asia/Tokyo");
        setCommunications(communicationsResult.status === "fulfilled" ? communicationsResult.value : { items: [] });

        if (scheduleResult.status === "rejected" || communicationsResult.status === "rejected") {
          setLoadError(`一部の${tenantLabels.teacher}情報の取得に失敗しました。担当詳細は表示しています。`);
        }

        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        if (error instanceof ApiError && error.status === 404) {
          setOccurrence(null);
          setLoadState("error");
          setLoadError("担当詳細が見つかりません。");
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "担当詳細の取得に失敗しました"));
      }
    }

    void loadDetail();

    return () => {
      cancelled = true;
    };
  }, [hasTeacherRole, isSessionReady, navigate, occurrenceId, tenantSlug]);

  const relatedCommunications = useMemo(
    () => (occurrence && communications ? communications.items.filter((communication) => communication.targetType === "occurrence" && communication.targetId === occurrence.occurrenceId) : []),
    [communications, occurrence]
  );

  async function refreshCommunications() {
    try {
      const nextCommunications = await fetchTeacherMemberCommunications(tenantSlug);
      setCommunications(nextCommunications);
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "関連する連絡事項の更新に失敗しました"));
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="teacher"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="担当詳細"
      description={`担当する${occurrenceLabel}の詳細と連絡事項を確認できます。編集は管理画面で行います。`}
      links={pageLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <TeacherAccessState
          routeStatus={routeStatus}
          sessionStatus={sessionStatus}
          errorMessage={errorMessage}
          hasTeacherRole={hasTeacherRole}
          labels={tenantLabels}
        />

        {loadState === "loading" ? <p>担当詳細を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {isSessionReady && hasTeacherRole && occurrence ? (
          <>
            <TeacherScheduleItemCard
              item={occurrence}
              tenantSlug={tenantSlug}
              timeZone={timeZone}
              showOccurrenceDate
              showExpectedStudents
              showTeacherSummary
              showOperatorNote
              showDetailLink={false}
              actions={(
                <TeacherOccurrenceCommunicationPanel
                  tenantSlug={tenantSlug}
                  item={occurrence}
                  timeZone={timeZone}
                  labels={tenantLabels}
                  onSent={refreshCommunications}
                />
              )}
            />

            <section style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 4 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>関連する連絡事項</h2>
                <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                  この担当予定に送信された連絡事項を表示します。
                </p>
              </div>
              {relatedCommunications.length === 0 ? <p style={{ margin: 0 }}>関連する連絡事項はありません。</p> : null}
              {relatedCommunications.map((communication) => (
                <MemberCommunicationCard
                  key={communication.communicationId}
                  communication={communication}
                  tenantSlug={tenantSlug}
                  thumbnailSurface="teacher"
                  labels={tenantLabels}
                  compact
                />
              ))}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
